import { useEffect, useState } from 'react';
import { get } from '../lib/api';
import { ErrorNote, Spinner } from '../components/ui';
import HistoryTable from '../components/HistoryTable';
import type { Badge, Board, PeriodSummary } from '../lib/types';

type Row = Omit<PeriodSummary, 'items'>;

/** The kid's history: streak trophies on top, paydays underneath. */
export default function QuestProgress(): JSX.Element {
  const [periodType, setPeriodType] = useState<'week' | 'month'>('week');
  const [rows, setRows] = useState<Row[] | null>(null);
  const [board, setBoard] = useState<Board | null>(null);
  const [badges, setBadges] = useState<Badge[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    get<{ board: Board }>('/api/board')
      .then((res) => setBoard(res.board))
      .catch((err) => setError((err as Error).message));
    get<{ badges: Badge[] }>('/api/badges')
      .then((res) => setBadges(res.badges))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    setRows(null);
    get<{ history: Row[] }>(`/api/history?periodType=${periodType}&count=8`)
      .then((res) => setRows(res.history))
      .catch((err) => setError((err as Error).message));
  }, [periodType]);

  const paid = rows?.filter((r) => r.payout?.status === 'paid').reduce((sum, r) => sum + (r.payout?.amount ?? 0), 0) ?? 0;
  const currency = rows?.[0]?.currency ?? '';

  return (
    <>
      <div className="q-hello">Your progress</div>
      <ErrorNote error={error} />

      <div className="q-stats">
        <div className="q-stat">
          <span className="q-stat-emoji">🔥</span>
          <span className="q-stat-num">{board ? board.streak.days : '–'}</span>
          <span className="q-stat-lab">day streak</span>
        </div>
        <div className="q-stat">
          <span className="q-stat-emoji">🛡️</span>
          <span className="q-stat-num">{board ? board.streak.shields : '–'}</span>
          <span className="q-stat-lab">shields held</span>
        </div>
        <div className="q-stat">
          <span className="q-stat-emoji">🏆</span>
          <span className="q-stat-num">{board ? board.streak.best : '–'}</span>
          <span className="q-stat-lab">best streak</span>
        </div>
        <div className="q-stat">
          <span className="q-stat-emoji">💰</span>
          <span className="q-stat-num">{rows ? paid : '–'}</span>
          <span className="q-stat-lab">{currency || 'paid'} so far</span>
        </div>
      </div>

      {board?.house && (
        <div className="q-house" style={{ marginTop: 12 }}>
          <span className="q-house-icon">🏠</span>
          <span className="grow">
            <b>House streak: {board.house.days} {board.house.days === 1 ? 'day' : 'days'}</b>
            <span className="q-goal-hint" style={{ display: 'block' }}>Best ever: {board.house.best} days</span>
          </span>
        </div>
      )}

      <h2>Badges</h2>
      {badges.length === 0 ? (
        <div className="q-goal" style={{ marginTop: 0 }}>
          <div className="q-goal-hint">
            Keep a streak for {board?.streak.nextMilestone ?? 7} days to win your first badge.
          </div>
        </div>
      ) : (
        <div className="q-badges">
          {badges.map((badge) => (
            <div key={badge.id} className="q-badge">
              <span className="q-badge-emoji">{badge.kind === 'house' ? '🏠' : badge.value >= 100 ? '👑' : badge.value >= 30 ? '🏆' : '🏅'}</span>
              <span className="q-badge-num">{badge.value}</span>
              <span className="q-badge-lab">{badge.kind === 'house' ? 'house days' : 'day streak'}</span>
            </div>
          ))}
        </div>
      )}

      <div className="chips" style={{ margin: '18px 0 12px' }}>
        {(['week', 'month'] as const).map((option) => (
          <button key={option} type="button" className={`chip${periodType === option ? ' on' : ''}`} onClick={() => setPeriodType(option)}>
            {option === 'week' ? 'By week' : 'By month'}
          </button>
        ))}
      </div>
      <div className="card">{rows ? <HistoryTable rows={rows} periodType={periodType} /> : <Spinner />}</div>
      <p className="tiny muted" style={{ marginTop: 12 }}>
        "Qualifies" means you did enough chores for pocket money. Mum and Dad make the final call.
      </p>
    </>
  );
}
